import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, RefreshCw, Search, Filter, AlertOctagon } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { tpmApi, deviceStatusLabel, deviceStatusColor } from '../../services/tpm'
import { formatDateTime } from '../../utils/format'

export default function TpmDevices() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const qc = useQueryClient()

  const STATUS_FILTERS = [
    { value: '', label: t('tpm.devices.statusFilters.all') },
    { value: '0', label: t('tpm.devices.statusFilters.produced') },
    { value: '1', label: t('tpm.devices.statusFilters.enrolled') },
    { value: '2', label: t('tpm.devices.statusFilters.active') },
    { value: '3', label: t('tpm.devices.statusFilters.suspended') },
    { value: '4', label: t('tpm.devices.statusFilters.revoked') },
    { value: '5', label: t('tpm.devices.statusFilters.decommissioned') },
  ]

  const { data: devices = [], isLoading, refetch } = useQuery({
    queryKey: ['tpm', 'devices', statusFilter],
    queryFn: () => tpmApi.listDevices(statusFilter || undefined),
  })

  const { data: pending = [] } = useQuery({
    queryKey: ['tpm', 'pending'],
    queryFn: () => tpmApi.listPending(),
    refetchInterval: 15000,
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, status, reason }) => tpmApi.changeStatus(id, status, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tpm', 'devices'] })
    },
  })

  const filtered = devices.filter((d) => {
    if (!search) return true
    const s = search.toLowerCase()
    return (
      d.ekPubSha256?.toLowerCase().includes(s) ||
      d.label?.toLowerCase().includes(s) ||
      d.lastVpnIp?.toLowerCase().includes(s) ||
      d.id?.toLowerCase().includes(s)
    )
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-theme-primary" />
            {t('tpm.devices.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('tpm.devices.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pending.length > 0 && (
            <Link
              to="/tpm/pending"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-100 text-amber-800 text-sm font-medium hover:bg-amber-200"
            >
              <AlertOctagon className="w-4 h-4" />
              {t(pending.length > 1 ? 'tpm.devices.pendingPlural' : 'tpm.devices.pending', { count: pending.length })}
            </Link>
          )}
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            {t('tpm.devices.refresh')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('tpm.devices.searchPlaceholder')}
            className="flex-1 outline-none text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">{t('tpm.devices.device')}</th>
              <th className="text-left px-4 py-2">{t('tpm.devices.status')}</th>
              <th className="text-left px-4 py-2">{t('tpm.devices.ekFingerprint')}</th>
              <th className="text-left px-4 py-2">{t('tpm.devices.vpnIp')}</th>
              <th className="text-left px-4 py-2">{t('tpm.devices.lastSeen')}</th>
              <th className="text-left px-4 py-2">{t('tpm.devices.recovery')}</th>
              <th className="text-right px-4 py-2">{t('tpm.devices.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">{t('tpm.devices.loading')}</td></tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">{t('tpm.devices.empty')}</td></tr>
            )}
            {filtered.map((d) => (
              <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2">
                  <Link to={`/tpm/devices/${d.id}`} className="text-theme-primary hover:underline">
                    {d.label || d.id.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-4 py-2">
                  <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', deviceStatusColor(d.status))}>
                    {deviceStatusLabel(d.status)}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-600">
                  {d.ekPubSha256?.slice(0, 16)}…
                </td>
                <td className="px-4 py-2 font-mono text-xs">{d.lastVpnIp || '—'}</td>
                <td className="px-4 py-2 text-xs text-gray-500">
                  {d.lastSeenAt ? formatDateTime(d.lastSeenAt) : '—'}
                </td>
                <td className="px-4 py-2 text-xs">
                  {d.hasRecoveryBlob ? (
                    <span className="text-green-600">{t('tpm.devices.recoveryYes')}</span>
                  ) : (
                    <span className="text-gray-400">{t('tpm.devices.recoveryNo')}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex gap-1">
                    {d.status !== 'Suspended' && d.status !== 'Revoked' && d.status !== 'Decommissioned' && (
                      <button
                        onClick={() => {
                          const reason = prompt(t('tpm.actions.suspendPrompt'))
                          if (reason) changeStatus.mutate({ id: d.id, status: 3, reason })
                        }}
                        className="text-xs px-2 py-1 rounded border border-amber-200 text-amber-700 hover:bg-amber-50"
                      >
                        {t('tpm.actions.suspend')}
                      </button>
                    )}
                    {d.status !== 'Revoked' && (
                      <button
                        onClick={() => {
                          const reason = prompt(t('tpm.actions.revokePrompt'))
                          if (reason) changeStatus.mutate({ id: d.id, status: 4, reason })
                        }}
                        className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                      >
                        {t('tpm.actions.revoke')}
                      </button>
                    )}
                    {(d.status === 'Suspended' || d.status === 'Enrolled') && (
                      <button
                        onClick={() => {
                          const reason = prompt(t('tpm.actions.reactivatePrompt'), 'manual_reactivation')
                          if (reason) changeStatus.mutate({ id: d.id, status: 2, reason })
                        }}
                        className="text-xs px-2 py-1 rounded border border-green-200 text-green-700 hover:bg-green-50"
                      >
                        {t('tpm.actions.activate')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
